import {
  type SimpleEvent,
  useSimpleEventSubscription
} from '@passionware/simple-event';
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  MoreHorizontal,
  Trash2
} from 'lucide-react';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import type {
  AnySpec,
  KindMap,
  KindOf,
  SpecOf,
  UnitValues
} from 'v5/core/form-schema/formKinds.types';
import type { ListUnit } from 'v5/core/form-schema/formSchema.types';
import { Badge } from 'v5/design-system/Badge';
import { Button, buttonVariants } from 'v5/design-system/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from 'v5/design-system/DropdownMenu';
import { SimpleTooltip } from 'v5/design-system/Tooltip';
import { useTranslation } from 'react-i18next';
import { cn } from 'v5/platform/dom/cn';
import { findFocusableElement } from 'v5/platform/dom/isFocusable';
import { useFieldContext } from './FieldContext';
import { RenderField } from './RenderField';
import { ListAddButton } from './ListAddButton';
import {
  type ListFormRow,
  newListFormRow,
  readRowValues
} from './listFormRows';
import { ListRowMotion } from './ListRowMotion';
import type { FormKit } from './schemaForm.types';
import { useNewRowsAppear } from './unitMotion';
import { fieldPath } from './unitPath';
import {
  SortableRows,
  useSortableRow
} from 'v5/patterns/forms/row-list/SortableRows';
import { kindHeading } from './ListLinesField';

export interface ListItemsFieldProps<M extends KindMap<M>> {
  unit: ListUnit<AnySpec<M>>;
  /** RHF path of the rows (`ListFormRow[]`) in the surrounding form. */
  name: string;
  kit: FormKit<M>;
  /**
   * Values of a new row (field keys as in the schema); `optionId` is the
   * template picked from the list's add action, `null` for a blank row.
   */
  newItem(optionId: string | null): UnitValues<M>;
  /** Add-action options already among the rows (a linked rate card): marked "Added". */
  usedAddOptions?(rows: ReadonlyArray<UnitValues<M>>): ReadonlyArray<string>;
  /**
   * Keys of the rows already saved: the others show as new (a tinted band
   * and a "New" badge). Omitted when creating a record, where every row is.
   */
  savedRowKeys?: ReadonlySet<string>;
  /** The unit's “+” (pane): adds a row as the add controls below do. */
  addSignal?: SimpleEvent<string | null>;
}

/**
 * Rows being edited sit edge to edge (their own padding spaces them), so a
 * row entering or leaving has no gap to ease.
 */
const EDIT_ROW_GAP = '0rem';

/**
 * The rows of a list unit, from its form: read as plain text in display
 * mode; in edit mode every row is an editor of its own, with its handle, move
 * up / down and remove always there, and the add controls below. The same
 * editor serves creating a resource and editing a list as a whole
 * (`ListUnitView`), whose form keeps the changes until Save.
 */
export function ListItemsField<M extends KindMap<M>>(
  props: ListItemsFieldProps<M>
) {
  const ctx = useFieldContext();
  return ctx.mode === 'edit' ? (
    <ListItemsEditor {...props} />
  ) : (
    <ListItemsDisplay {...props} />
  );
}

function ListItemsDisplay<M extends KindMap<M>>({
  unit,
  name,
  kit
}: ListItemsFieldProps<M>) {
  const rows =
    useWatch<Record<string, ListFormRow<M>[] | undefined>>({ name }) ?? [];
  // An empty list shows only its unit's title and "no value".
  if (rows.length === 0) return null;
  return (
    <div className="flex flex-col gap-y-4">
      {rows.map((row, index) => (
        <div key={row.rowKey} className="flex flex-col gap-2">
          <RowFields unit={unit} name={name} index={index} kit={kit} />
        </div>
      ))}
    </div>
  );
}

function ListItemsEditor<M extends KindMap<M>>({
  unit,
  name,
  kit,
  newItem,
  usedAddOptions,
  savedRowKeys,
  addSignal
}: ListItemsFieldProps<M>) {
  const { t } = useTranslation('patterns');
  const form = useFormContext();
  const { fields, append, remove, move } = useFieldArray({
    control: form.control,
    name
  });
  const rows =
    useWatch<Record<string, ListFormRow<M>[] | undefined>>({ name }) ?? [];
  const appear = useNewRowsAppear();
  // A removed row collapses first and leaves the form after: its fields'
  // paths shift on removal, so it cannot stay on screen once removed.
  const [leaving, setLeaving] = useState<ReadonlySet<string>>(() => new Set());
  const latestFields = useRef(fields);
  latestFields.current = fields;
  const rowsRef = useRef<HTMLDivElement>(null);
  const focusNewRow = useRef(false);

  const itemTitle = unit.item.title;
  const ids = fields.map(field => field.id);

  // A row just added takes the focus (its first field); it grows in and
  // scrolls itself into view meanwhile (`ListRowMotion`).
  useEffect(() => {
    if (!focusNewRow.current) return;
    focusNewRow.current = false;
    const last = rowsRef.current?.querySelector<HTMLElement>(
      `[data-list-row-fields="${fields.length - 1}"]`
    );
    if (last) findFocusableElement(last)?.focus({ preventScroll: true });
  }, [fields.length]);

  function add(optionId: string | null) {
    focusNewRow.current = true;
    append(newListFormRow(newItem(optionId)), { shouldFocus: false });
  }
  useSimpleEventSubscription(addSignal?.addListener, add);

  function removeRow(id: string) {
    const index = latestFields.current.findIndex(field => field.id === id);
    if (index !== -1) remove(index);
    setLeaving(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  const used = usedAddOptions?.(
    rows.map(row => readRowValues(unit.item.fields, row.values))
  );

  return (
    // Room above and below keeps a new row's tint off the unit's title and
    // the add buttons.
    <div ref={rowsRef} className="flex flex-col pt-2">
      <SortableRows ids={ids} itemTitle={itemTitle} onMove={move}>
        {fields.map((field, index) => {
          const label = `${itemTitle} ${index + 1}`;
          const rowKey = rows[index]?.rowKey;
          const isNew =
            savedRowKeys !== undefined &&
            rowKey !== undefined &&
            !savedRowKeys.has(rowKey);
          return (
            <ListRowMotion
              key={field.id}
              gap={EDIT_ROW_GAP}
              appear={appear}
              present={!leaving.has(field.id)}
              onGone={() => removeRow(field.id)}
              reveal
            >
              <SortableRow
                id={field.id}
                label={label}
                sortable={fields.length > 1}
              >
                {(handle, isDragging) => (
                  // No box per row: in the unit's frame that reads as a
                  // card in a card. Each row opens with a band across the
                  // frame (its padding, `UnitLayout`) naming the row, and a
                  // gap sets it off from the row above; a dragged row lifts.
                  <div className={cn('-mx-2', index > 0 && 'pt-4')}>
                    <div
                      className={cn(
                        'flex flex-col',
                        isDragging &&
                          'overflow-hidden rounded-lg bg-bg-default shadow-md ring-1 ring-border-default'
                      )}
                    >
                      <div
                        data-testid="list-row-header"
                        className={cn(
                          'flex items-center justify-between gap-2 border-y border-border-default bg-bg-subtle py-1.5 pr-2 pl-1.5',
                          // A new row's band is tinted, an accent bar at its start.
                          isNew &&
                            'bg-bg-accent-2 shadow-[inset_2px_0_0_var(--color-border-accent)]',
                          isDragging && 'border-t-0'
                        )}
                      >
                        <span className="flex min-w-0 items-center gap-1">
                          {handle}
                          <RowHeading
                            unit={unit}
                            name={name}
                            index={index}
                            kit={kit}
                            label={label}
                          />
                        </span>
                        <span className="flex shrink-0 items-center gap-1">
                          {isNew && (
                            <SimpleTooltip title={t('SchemaForm.badge.newRow')}>
                              <Badge variant="accent" tone="subtle" size="sm">
                                New
                              </Badge>
                            </SimpleTooltip>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                layout="icon"
                                className="shrink-0"
                                aria-label={`${label} actions`}
                              >
                                <MoreHorizontal />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {fields.length > 1 && (
                                <>
                                  <DropdownMenuItem
                                    disabled={index === 0}
                                    onSelect={() => move(index, index - 1)}
                                  >
                                    <ArrowUp aria-hidden />
                                    Move up
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    disabled={index === fields.length - 1}
                                    onSelect={() => move(index, index + 1)}
                                  >
                                    <ArrowDown aria-hidden />
                                    Move down
                                  </DropdownMenuItem>
                                  <DropdownMenuSeparator />
                                </>
                              )}
                              <DropdownMenuItem
                                className="text-fg-danger focus:text-fg-danger"
                                onSelect={() =>
                                  setLeaving(prev =>
                                    new Set(prev).add(field.id)
                                  )
                                }
                              >
                                <Trash2 aria-hidden />
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </span>
                      </div>
                      <div
                        className="flex flex-col gap-2 px-2 pt-2.5 pb-1"
                        data-list-row-fields={index}
                      >
                        <RowFields
                          unit={unit}
                          name={name}
                          index={index}
                          kit={kit}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </SortableRow>
            </ListRowMotion>
          );
        })}
      </SortableRows>
      <div className="pt-2">
        <ListAddButton add={unit.add} usedOptions={used} onAdd={add} />
      </div>
    </div>
  );
}

/** The row's single field, when it has exactly one. */
function singleField<S>(fields: ReadonlyArray<S>): S | null {
  return fields.length === 1 ? fields[0] : null;
}

/**
 * The row's name in its header: the kit's heading for the kind of the row's
 * single field, else numbered ("Rate 2").
 */
function RowHeading<M extends KindMap<M>>({
  unit,
  name,
  index,
  kit,
  label
}: {
  unit: ListUnit<AnySpec<M>>;
  name: string;
  index: number;
  kit: FormKit<M>;
  /** "Rate 2". */
  label: string;
}) {
  const field = singleField(unit.item.fields);
  const heading =
    field === null
      ? null
      : kindHeading(
          kit,
          field,
          fieldPath(`${name}.${index}.values`, field.key)
        );
  return (
    heading ?? (
      <span className="truncate font-semibold text-fg-default">{label}</span>
    )
  );
}

function RowFields<M extends KindMap<M>>({
  unit,
  name,
  index,
  kit
}: {
  unit: ListUnit<AnySpec<M>>;
  name: string;
  index: number;
  kit: FormKit<M>;
}) {
  return (
    <>
      {unit.item.fields.map(field => (
        <RenderField
          key={field.key}
          kit={kit}
          spec={field}
          path={fieldPath(`${name}.${index}.values`, field.key)}
          hideLabel={unit.item.fields.length === 1}
        />
      ))}
    </>
  );
}

/**
 * One row as a sortable item. The handle is the drag activator: pointer drag
 * after 4px, or Space / Enter to pick up, arrows to move and Space / Enter to
 * drop (Escape cancels), announced to screen readers. Move up / down in the
 * row's menu stay for people who cannot drag (WCAG 2.5.7).
 */
function SortableRow({
  id,
  label,
  sortable,
  children
}: {
  id: string;
  /** Names the row for the handle, e.g. "Rate 2". */
  label: string;
  /** Two rows or more: the handle exists. */
  sortable: boolean;
  children(handle: ReactNode, isDragging: boolean): ReactNode;
}) {
  const { rowProps, handleProps, isDragging } = useSortableRow(id, sortable);

  const handle = !sortable ? null : (
    <SimpleTooltip title="Drag to reorder">
      <button
        type="button"
        {...handleProps}
        aria-label={`Reorder ${label}`}
        className={cn(
          buttonVariants({ variant: 'ghost', size: 'xs', layout: 'icon' }),
          '-ml-1 h-6 w-5 min-h-0 min-w-0 shrink-0 p-0 text-fg-muted',
          'touch-none select-none cursor-grab active:cursor-grabbing'
        )}
      >
        <GripVertical className="size-4" />
      </button>
    </SimpleTooltip>
  );

  return <div {...rowProps}>{children(handle, isDragging)}</div>;
}
